import { BetaBadge, Button } from '@consigli/facade';
import {
  BuildingPackageType,
  DocumentPackageType,
  LayoutType,
  MeetingSecretaryType,
  QaAgentType,
  SolutionViewerType,
  StructuralType,
} from '@consigli/types';
import clsx from 'clsx';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { LuBadgeCheck } from 'react-icons/lu';
import { Tooltip } from 'react-tooltip';

import { useServiceNavigation } from '@/features/project-services/use-service-navigation';
import { Route } from '@/routes';

interface ServiceCardProps {
  id: string | undefined;
  title: string;
  icon: string;
  active?: boolean;
  isBeta?: boolean;
  tooltip?: string;
  serviceRoute?: string;
  newServiceRoute?: string;
  serviceId?:
    | DocumentPackageType
    | LayoutType
    | StructuralType
    | BuildingPackageType
    | MeetingSecretaryType
    | SolutionViewerType
    | QaAgentType;
  projectId: number;
  externalToSolutionViewer?: boolean;
  externalUrl?: string;
  openInSameWindow?: boolean;
}

export const ServiceCard: FC<ServiceCardProps> = ({
  id,
  title,
  icon,
  active,
  isBeta,
  tooltip,
  newServiceRoute,
  serviceRoute,
  serviceId,
  projectId,
  externalToSolutionViewer,
  externalUrl,
  openInSameWindow,
}) => {
  const { t } = useTranslation();
  const { navigateToService } = useServiceNavigation({
    active,
    externalToSolutionViewer,
    externalUrl,
    id,
    newServiceRoute,
    openInSameWindow,
    projectId,
    serviceRoute,
    serviceId,
  });

  const titleText = useMemo(() => {
    if (externalUrl) {
      return t('service-tooltips.title-text-external', { serviceName: title });
    }
    if (serviceRoute === Route.REPORTS) {
      return t('service-tooltips.title-text-reports');
    }
    if (
      serviceRoute === Route.TENDER_DOCUMENTS ||
      serviceRoute === Route.MANAGEMENT_OPERATIONS_MAINTENANCE ||
      serviceRoute === Route.OFFER ||
      serviceRoute === Route.BID_REVIEW ||
      serviceRoute === Route.TENANT_SPECIFICATION ||
      serviceRoute === Route.DUE_DILIGENCE ||
      serviceRoute === Route.SITE_DUE_DILIGENCE ||
      serviceRoute === Route.DESIGN_PACKAGE_REVIEW ||
      serviceRoute === Route.RFP_RFQ_REVIEW ||
      serviceRoute === Route.CONTRACT_REVIEW
    ) {
      return t('service-tooltips.title-text-insight', { serviceName: title });
    }
    if (serviceRoute === Route.SPACE_ANALYSIS) {
      return t('service-tooltips.title-text-space-analysis');
    }
    if (serviceRoute === Route.SCHEDULE_CHECK) {
      return t('service-tooltips.title-text-schedule-check');
    }
    if (serviceRoute === Route.VERSION_COMPARE) {
      return t('service-tooltips.title-text-version-compare', { serviceName: title });
    }
    return t('service-tooltips.title-text-optimization', { serviceName: title });
  }, [externalUrl, serviceRoute, t, title]);

  const ServiceTooltip = () => (
    <Tooltip
      id={id}
      place="top-end"
      opacity={100}
      classNameArrow="hidden"
      // There is some issue with the tooltip library and Tailwind v4, we have to add ! to override the default styles.
      className="!bg-day-light-5 !p-4 !h-auto !w-72 !text-day-neutral-subtle !rounded-tl-[24px] !rounded-bl-[24px] !rounded-tr-[24px] !shadow-[0_1px_2px_0_rgba(0,0,0,0.06),0_1px_3px_0_rgba(0,0,0,0.10)] !z-10"
    >
      <div className="text-xs text-left py-4">{tooltip}</div>
      <div className="text-xs font-semibold text-left">{titleText}</div>
    </Tooltip>
  );

  return (
    <div className="relative group">
      <div
        className={clsx(
          'flex flex-col items-center bg-day-light-4 gap-4 rounded-[24px] shadow-[0_1px_2px_0_rgba(0,0,0,0.06),0_1px_3px_0_rgba(0,0,0,0.10)] focus:outline-hidden focus:ring-light-grey border-4 border-day-light-4 cursor-pointer',
        )}
        onClick={navigateToService}
        //Makes the non-interactive element <div> behave like a button, but without nested button warnings
        tabIndex={0}
        // A11Y: role="button" promises both Enter and Space activation.
        //
        // A real button takes Space on keyup, and that is not a detail: keydown
        // auto-repeats while a key is held, so activating there fires once per
        // repeat. navigateToService opens a window for external-URL cards, and
        // every repeat is its own trusted gesture, so the popup blocker does not
        // absorb them — holding Space for a second opens tabs by the dozen. The
        // `repeat` guard below is what a native button gets for free.
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            // Above the guard: a held key must still not scroll the page, even
            // though it must not re-activate the card.
            event.preventDefault();
            if (event.repeat) return;
            navigateToService();
          }
        }}
        role="button"
      >
        <div className="relative group overflow-hidden rounded-t-[24px] w-full">
          <div
            className={clsx(
              active ? 'top-[calc(0%-80px)]' : 'top-[calc(0%-150px)]',
              'overflow-hidden absolute w-full h-full  group-hover:top-0 transition-all duration-500 ease-in-out rounded-tl-[24px] rounded-tr-[24px] group-hover:rounded-b-[24px] bg-day-light-3',
            )}
          />
          <div className="relative flex flex-col justify-evenly items-center text-center w-full">
            {isBeta && (
              <div className="absolute top-3 left-3 z-10">
                <BetaBadge />
              </div>
            )}
            <div className="w-full flex justify-center items-center px-6 pt-6">
              <img
                src={icon}
                alt="services"
                className="transition-transform duration-200 ease-in-out h-32 group-hover:scale-125"
              />
            </div>
            <div className="flex flex-row justify-between items-center w-full p-6 h-[75px]">
              {active && <LuBadgeCheck size={'1.5em'} color="#00A16E" />}
              <div
                className={clsx(
                  !active && 'text-left',
                  'text-base w-full px-2 flex-1 pr-4 font-semibold text-day-neutral-dark max-w-[165px] break-words',
                )}
                title={title}
              >
                {title}
              </div>
              <HiOutlineInformationCircle size={'1.5em'} data-tooltip-id={id} color={'#334155'} />
            </div>
          </div>
          {active ? null : (
            <div className="flex flex-col justify-evenly items-center w-full pb-6 px-6">
              <Button type="button" secondary rounded className="w-full z-10" icon={FaPlus}>
                {t('service-list.add')}
              </Button>
            </div>
          )}
        </div>
      </div>
      {tooltip && <ServiceTooltip />}
    </div>
  );
};
